import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, FileDown, Building2, DoorOpen, Users, UserCheck } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const HostelReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State for students list
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalStudents, setTotalStudents] = useState(0);

  useEffect(() => {
    fetchReport();
    apiClient.get('/api/tenants/my-college/settings').then(r => setCollegeInfo(r.data)).catch(() => {});
  }, [currentPage, itemsPerPage]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      // Use apiClient which includes auth token
      const hostelsRes = await apiClient.get('/api/hostel/hostels').catch(() => ({ data: [] }));
      const roomsRes = await apiClient.get('/api/hostel/rooms').catch(() => ({ data: [] }));
      const studentsRes = await apiClient.get(`/api/hostel/students?${params.toString()}`).catch(() => ({ data: [] }));
      
      const hostels = Array.isArray(hostelsRes.data) ? hostelsRes.data : [];
      const rooms = Array.isArray(roomsRes.data) ? roomsRes.data : [];
      
      // Handle paginated students response
      let students = [];
      let totalStudentsCount = 0;
      if (Array.isArray(studentsRes.data)) {
        students = studentsRes.data;
        totalStudentsCount = students.length;
      } else {
        students = studentsRes.data?.data || [];
        totalStudentsCount = studentsRes.data?.total || students.length;
      }
      
      setTotalStudents(totalStudentsCount);
      
      setReport({
        totalHostels: hostels.length,
        totalRooms: rooms.length,
        occupiedRooms: rooms.filter(r => (r.current_occupancy || 0) > 0).length,
        totalCapacity: rooms.reduce((sum, r) => sum + (r.capacity || 0), 0),
        totalStudents: totalStudentsCount,
        activeStudents: students.filter(s => s.status === 'Active').length,
        hostels: hostels,
        rooms: rooms,
        students: students
      });
    } catch (error) {
      console.error('Error:', error);
      setReport({
        totalHostels: 0, totalRooms: 0, occupiedRooms: 0, totalCapacity: 0,
        totalStudents: 0, activeStudents: 0, hostels: [], rooms: [], students: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportToPDF = async () => {
    if (!report) return;
    if (exporting) return;
    setExporting(true);
    
    try {
      // Fetch all students for export (without pagination)
      const studentsRes = await apiClient.get('/api/hostel/students?limit=1000').catch(() => ({ data: [] }));
      
      let allStudents = [];
      if (Array.isArray(studentsRes.data)) {
        allStudents = studentsRes.data;
      } else {
        allStudents = studentsRes.data?.data || [];
      }
      
      const stats = {
        'Total Hostels': report.totalHostels,
        'Total Rooms': report.totalRooms,
        'Occupied Rooms': report.occupiedRooms,
        'Total Students': allStudents.length,
        'Active Students': allStudents.filter(s => s.status === 'Active').length
      };

      const columns = [
        { header: 'Student Name', dataKey: 'name' },
        { header: 'Room No', dataKey: 'room' },
        { header: 'Hostel', dataKey: 'hostel' },
        { header: 'Check-in Date', dataKey: 'checkin' },
        { header: 'Status', dataKey: 'status' }
      ];

      const data = allStudents.map(s => ({
        name: s.student_name || 'N/A',
        room: s.room_number || 'N/A',
        hostel: s.hostel_name || 'N/A',
        checkin: s.admission_date || s.check_in_date || 'N/A',
        status: s.status || 'N/A'
      }));

      exportSummaryReportToPDF('Hostel Occupancy Report', stats, columns, data, 'hostel_report.pdf', collegeInfo);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Hostel Report
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Refresh'}
            </Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={!report || loading || exporting}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {report && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Total Hostels</p>
                      <p className="text-2xl font-bold text-blue-700">{report.totalHostels}</p>
                    </div>
                    <Building2 className="w-8 h-8 text-blue-400" />
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600">Total Rooms</p>
                      <p className="text-2xl font-bold text-purple-700">{report.totalRooms}</p>
                    </div>
                    <DoorOpen className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600">Occupied</p>
                      <p className="text-2xl font-bold text-yellow-700">{report.occupiedRooms}</p>
                    </div>
                    <DoorOpen className="w-8 h-8 text-yellow-400" />
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600">Total Students</p>
                      <p className="text-2xl font-bold text-green-700">{report.totalStudents}</p>
                    </div>
                    <Users className="w-8 h-8 text-green-400" />
                  </div>
                </div>
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-cyan-600">Active</p>
                      <p className="text-2xl font-bold text-cyan-700">{report.activeStudents}</p>
                    </div>
                    <UserCheck className="w-8 h-8 text-cyan-400" />
                  </div>
                </div>
              </div>

              {/* Hostel Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Hostel Details</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Hostel Name</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-right p-3">Total Rooms</th>
                        <th className="text-right p-3">Capacity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.hostels.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center p-8 text-gray-500">
                            <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>No hostels found</p>
                          </td>
                        </tr>
                      ) : (
                        report.hostels.map((hostel, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{hostel.hostel_name || hostel.name}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                hostel.hostel_type === 'Boys' ? 'bg-blue-100 text-blue-800' :
                                hostel.hostel_type === 'Girls' ? 'bg-pink-100 text-pink-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {hostel.hostel_type || 'N/A'}
                              </span>
                            </td>
                            <td className="p-3 text-right">{hostel.total_rooms || 0}</td>
                            <td className="p-3 text-right">{hostel.total_capacity || 0}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Students in Hostel */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Hostel Students ({report.totalStudents})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Student Name</th>
                        <th className="text-left p-3">Hostel</th>
                        <th className="text-left p-3">Room No</th>
                        <th className="text-left p-3">Check-in Date</th>
                        <th className="text-center p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.students.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center p-8 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>No students in hostel</p>
                          </td>
                        </tr>
                      ) : (
                        report.students.map((student, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{student.student_name || 'N/A'}</td>
                            <td className="p-3">{student.hostel_name || 'N/A'}</td>
                            <td className="p-3">{student.room_number || 'N/A'}</td>
                            <td className="p-3">{student.admission_date || student.check_in_date || 'N/A'}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                student.status === 'Active' ? 'bg-green-100 text-green-800' :
                                student.status === 'Checked Out' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {student.status || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  
                  {/* Pagination for Students */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalStudents / itemsPerPage)}
                    totalItems={totalStudents}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HostelReport;
